/**
 * GlobalSearch - Types, constants, and helper mappings
 */

import {
  Briefcase,
  FileText,
  File,
  Mail,
  User,
  ClipboardCheck,
  AlertTriangle,
  Plus,
  Upload,
  Sparkles,
  Filter,
  Compass,
  ArrowRight,
} from 'lucide-react'
import type { ExternalLink } from '../types'

// Types
//
// `SearchResult.type` is a string (not a closed union) so module
// search providers can introduce their own result kinds (e.g.
// "ticket", "module", "customer") without changing `@quotech/ui`.
// The well-known platform kinds are still listed in
// `KNOWN_SEARCH_RESULT_TYPES` for icon/colour fallbacks.
export type SearchResultType = string

export interface SearchResult {
  id: string
  type: SearchResultType
  title: string
  subtitle?: string
  description?: string
  icon: string
  color: string
  url: string
  similarity?: number
  search_type?: 'keyword' | 'semantic'
  metadata?: Record<string, any>
  /** When provided, identifies the `SearchProvider` that emitted
   *  this result. Used in the UI for "from {provider}" chips and
   *  for de-duplicating across providers. */
  providerId?: string
  /** Optional grouping key for the board-first sectioned palette
   *  (2026-06-03). One of `SEARCH_SECTIONS`' keys — e.g. `'board'`
   *  (current board), `'jobs'`, `'emails'`, `'otherBoards'`. When
   *  ANY result carries a section, `useSearch` stable-sorts results
   *  by `SEARCH_SECTIONS[section].order` and `SearchResults` renders
   *  a header per section. When NO result carries a section the
   *  palette renders the flat single-"Results" list exactly as
   *  before — so existing hosts (master SPA) are untouched. */
  section?: string
}

/**
 * Section registry for the board-first sectioned palette.
 *
 * `order` drives the on-screen sequence (lower = higher up);
 * `label` is the section header text. Results whose `section` is
 * unknown sort AFTER all known sections (see `sectionOrder`), so a
 * provider can introduce a new section id without code changes here
 * — it just lands at the bottom until given an entry.
 */
export const SEARCH_SECTIONS: Record<
  string,
  { order: number; label: string }
> = {
  // Navigation/hub destinations sit ABOVE everything: when the query
  // names a place ("email" → Email hub), the operator's intent is "take
  // me there" first, content second. Only STRONG nav matches get tagged
  // with this section (see createNavSearchProvider) so fuzzy nav hits
  // never bury the content tiers below.
  pages: { order: -1, label: 'Pages' },
  board: { order: 0, label: 'On this board' },
  jobs: { order: 1, label: 'Jobs' },
  emails: { order: 2, label: 'Emails' },
  otherBoards: { order: 3, label: 'Other boards' },
}

/** Pure I→O — resolve a section id to its sort order. Unknown /
 *  absent sections sort last (large sentinel) but keep a stable
 *  relative order via the merge's arrival index. */
export function sectionOrder(section?: string): number {
  if (!section) return Number.MAX_SAFE_INTEGER
  return SEARCH_SECTIONS[section]?.order ?? Number.MAX_SAFE_INTEGER - 1
}

/**
 * Module-supplied search backend.
 *
 * A `SearchProvider` is the modular extension point of
 * `<GlobalSearch>`: each module (Email, QDrive, Customers, Jobs,
 * Tickets, …) can register its own provider, the palette fans
 * out to all of them in parallel, results are merged and
 * deduplicated. Hosts that don't register any providers see
 * exactly the same behaviour as before — the existing
 * `apiClient.post('/api/search/global/')` codepath is untouched.
 *
 * Contract:
 *   - `search(query, signal)` MUST return quickly. Long-running
 *     work belongs behind a debounce; `useSearch` already
 *     debounces by 200ms before calling.
 *   - The provider MUST honour `signal` and abort in-flight
 *     work when the query changes. Use `fetch(url, { signal })`
 *     or call `signal.throwIfAborted()` between awaits.
 *   - Results returned after `signal.aborted` will be discarded
 *     by the consumer; the provider need not check `aborted`
 *     itself if the API client supports `signal`.
 *   - Errors thrown from `search()` are isolated per provider.
 *     One provider failing does NOT take down other providers
 *     or the global search call. The error surfaces in
 *     `useSearch().searchError` with the provider id.
 */
export interface SearchProvider {
  /** Stable id used for de-dup, telemetry, and the
   *  filter-bar chip. e.g. "email", "qdrive", "customers". */
  id: string
  /** Human-readable label for the filter-bar chip. */
  label: string
  /** Lucide icon component for the filter chip. Optional —
   *  the filter list falls back to a generic icon. */
  icon?: any
  /** When the active filter set is non-empty, the provider
   *  is only consulted if its `id` (or any value in this
   *  list) is among the active filters. Defaults to `[id]`. */
  filterIds?: string[]
  /** Run the search. Honour `signal` to abort. */
  search: (
    query: string,
    signal: AbortSignal,
  ) => Promise<SearchResult[]>
}

/** Built-in result types. Hosts and providers may add their
 *  own; the icon / colour mapping below falls back gracefully. */
export const KNOWN_SEARCH_RESULT_TYPES = [
  'job',
  'quote',
  'file',
  'email',
  'contact',
  'inspection',
  'ncr',
] as const

export interface QuickAction {
  id: string
  label: string
  shortcut?: string
  icon: string
  url?: string
  action?: () => void
}

/** Local job data for instant client-side search (no API needed) */
export interface LocalJob {
  job_id: string | number
  client?: string
  site?: string
  name?: string
  status?: string
  description?: string
}

export interface QuickNavItem {
  id: string
  label: string
  description?: string
  icon: any
  href: string
  color: string
}

export interface QuickFilter {
  id: string
  label: string
  query: string
  icon: any
  color: string
}

export interface GlobalSearchProps {
  isOpen: boolean
  onClose: () => void
  onSearch?: (query: string, results: SearchResult[]) => void
  apiClient?: {
    post: (url: string, data: any) => Promise<any>
    get: (url: string) => Promise<any>
  }
  localJobs?: LocalJob[]
  quickActions?: QuickAction[]
  quickNavItems?: QuickNavItem[]
  quickFilters?: QuickFilter[]
  recentJobs?: Array<{ id: string; name: string; client?: string; status?: string }>
  /** Module-supplied search providers. Each is consulted in
   *  parallel with the legacy `apiClient.post` call; results are
   *  merged and deduplicated by `${type}-${id}`. Hosts that
   *  don't register any providers see no behaviour change. */
  searchProviders?: SearchProvider[]
  /** Stack 11 PR 2 — initial set of active filter chip ids. When
   *  non-empty, the popup opens with the filter bar visible and
   *  the named filters pre-selected. Combined with
   *  ``placeholder``, this lets a host repurpose ``<GlobalSearch>``
   *  as a scoped advanced-search popup (e.g. Cmd+F on the Email
   *  Hub opens with ``initialActiveFilters=['email','card','board']``
   *  so the user starts with the Cards + Emails fan-out and can
   *  click chips to widen). Default ``[]``. */
  initialActiveFilters?: string[]
  /** Stack 11 PR 2 — override the search input placeholder.
   *  Default ``"Search jobs, quotes, files, emails..."``. Hosts
   *  that pre-select filters (see ``initialActiveFilters``)
   *  should override this so the popup's prompt matches the
   *  scope ("Search cards and emails…"). */
  placeholder?: string
  /** BUG-2026-05-27-KANBAN-SEARCH-NOT-BOARD-SCOPED — pre-fill
   *  the palette input on the next ``isOpen → true`` transition.
   *
   *  Hosts use this to forward an in-context query (e.g. the
   *  in-board kanban search term) into the global palette when
   *  the operator clicks "Search across all boards" on an empty
   *  in-board result. Cleared after consumption so manual
   *  ⌘F / ⌘K re-opens start blank (the host is responsible for
   *  resetting its own seeding state inside ``onClose``).
   *
   *  Empty string is treated the same as ``undefined``. */
  initialQuery?: string
  /** Always-visible "Hubs" launcher bar, pinned to the bottom of the
   *  palette (below results, above the keyboard hints). Each entry is
   *  the same `ExternalLink` the sidebar Hubs section used, so click
   *  behaviour is identical when paired with `onPinnedHubClick`.
   *  Relocates cross-hub navigation OUT of the sidebar rail and INTO
   *  search (2026-06-13 product decision). Empty / undefined → no bar
   *  (existing hosts unchanged). */
  pinnedHubs?: (ExternalLink & { status?: 'production' | 'development' | 'planned' })[]
  /** Click handler for `pinnedHubs` entries. Mirrors the sidebar's
   *  `onHubClick(hubId, href)` — opens consolidated hubs as in-shell
   *  tabs / SSO-redirects external hubs. When absent, the bar falls
   *  back to `window.location.href = href`. */
  onPinnedHubClick?: (hubId: string, href: string) => void
  /** Currently-active hub id — highlights the matching `pinnedHubs`
   *  chip. Optional. */
  currentHubId?: string
  /** Tailwind max-width class for the modal panel. Default
   *  ``'max-w-3xl'``. The platform shell widens this (``max-w-5xl``)
   *  now that the palette doubles as the hub launcher. */
  panelMaxWidthClassName?: string
}

// Icon mapping
export const getIcon = (iconName: string) => {
  const icons: Record<string, any> = {
    Briefcase,
    FileText,
    File,
    Mail,
    User,
    ClipboardCheck,
    AlertTriangle,
    Plus,
    Upload,
    Sparkles,
    Filter,
    Compass,
    ArrowRight,
  }
  return icons[iconName] || File
}

// Color mapping
export const getColorClass = (color: string) => {
  const colors: Record<string, string> = {
    blue: 'text-blue-500 bg-blue-500/10',
    purple: 'text-purple-500 bg-purple-500/10',
    green: 'text-green-500 bg-green-500/10',
    orange: 'text-orange-500 bg-orange-500/10',
    teal: 'text-teal-500 bg-teal-500/10',
    emerald: 'text-emerald-500 bg-emerald-500/10',
    red: 'text-red-500 bg-red-500/10',
  }
  return colors[color] || 'text-gray-500 bg-gray-500/10'
}

// Type labels
export const typeLabels: Record<string, string> = {
  job: 'Job',
  quote: 'Quote',
  file: 'File',
  email: 'Email',
  contact: 'Contact',
  inspection: 'QA Inspection',
  ncr: 'NCR',
  diary: 'Diary',
  smartdocs: 'SmartDocs',
  scheduler: 'Scheduler',
}

// Default quick actions
export const defaultQuickActions: QuickAction[] = [
  { id: 'new-quote', label: 'Create New Quote', shortcut: '⌘ N', icon: 'Plus', url: 'https://crm.quotech.ai/quotes/new' },
  { id: 'upload-file', label: 'Upload File to QDrive', shortcut: '⌘ U', icon: 'Upload', url: 'https://qdrive.quotech.ai/upload' },
  { id: 'new-inspection', label: 'Create QA Inspection', shortcut: '⌘ I', icon: 'ClipboardCheck', url: 'https://qa.quotech.ai/records/new' },
]

export const defaultQuickNav: QuickNavItem[] = [
  { id: 'dashboard', label: 'Dashboard', description: 'Overview & KPIs', icon: Briefcase, href: '/dashboard', color: 'teal' },
  { id: 'jobs', label: 'Active Jobs', description: 'All current projects', icon: Briefcase, href: '/jobs', color: 'blue' },
  { id: 'job-performance', label: 'Job Performance', description: 'Health & variance report', icon: FileText, href: '/reports/job-performance', color: 'purple' },
  { id: 'quotes', label: 'Quotes Pipeline', description: 'CRM & quotes', icon: FileText, href: 'https://crm.quotech.ai', color: 'green' },
  { id: 'qa', label: 'QA Hub', description: 'Inspections & NCRs', icon: ClipboardCheck, href: 'https://qa.quotech.ai', color: 'orange' },
  { id: 'qdrive', label: 'QDrive', description: 'Files & documents', icon: File, href: 'https://qdrive.quotech.ai', color: 'emerald' },
]

export const defaultQuickFilters: QuickFilter[] = [
  { id: 'at-risk', label: 'Jobs at Risk', query: 'status:at-risk', icon: AlertTriangle, color: 'red' },
  { id: 'wip', label: 'Active Works', query: 'status:wip', icon: Briefcase, color: 'blue' },
  { id: 'my-jobs', label: 'My Jobs', query: 'assignee:me', icon: User, color: 'purple' },
  { id: 'recent-invoiced', label: 'Recently Invoiced', query: 'invoiced:7d', icon: FileText, color: 'green' },
]

// Filter options for search.
// Stack 11 PR 2 — added ``card`` / ``board`` / ``lane`` so the
// Cmd+F advanced search popup can scope to kanban result kinds
// independently of the catch-all ``kanban`` provider chip.
export const filterOptions = [
  { id: 'job', label: 'Jobs', icon: Briefcase },
  { id: 'quote', label: 'Quotes', icon: FileText },
  { id: 'file', label: 'Files', icon: File },
  { id: 'email', label: 'Emails', icon: Mail },
  { id: 'card', label: 'Cards', icon: FileText },
  { id: 'board', label: 'Boards', icon: Briefcase },
  { id: 'lane', label: 'Lanes', icon: Filter },
  { id: 'contact', label: 'Contacts', icon: User },
  { id: 'qa', label: 'QA', icon: ClipboardCheck },
]
